import type { ReactElement } from 'react';
import { Box } from '@mantine/core';
import { Activity } from 'lucide-react';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import PageLevelFilterRow from '@/shared/surfaces/PageLevelFilterRow';
import SurfaceAssistScope, { SurfaceAssistToolbar } from '@/shared/surfaces/assist/SurfaceAssistScope';
import { SurfaceActivityTab } from '@/shared/surfaces/SurfaceActivityTabsBody';
import type { ActivityOrder } from '@/shared/surfaces/SurfaceActivityViewModel';
import { useSurface } from '@/shared/surfaces/useSurface';
import TopicNotFound from '@/shared/surfaces/TopicNotFound';
import { TOKEN_USAGE_INTERACTION_COLUMNS } from '@/features/token-usage/TokenUsageInteractionColumns';

const HEAVIEST_FIRST: ActivityOrder = { column: 'outputTokens', direction: 'desc' };

/** Spend › Tokens › Activity: the consumption turn feed, heaviest prompts first, with in/out/total tokens and allocated cost. */
export default function SpendTokensActivityPage(): ReactElement {
    const surface = useSurface('token-usage');
    if (surface === undefined) return <TopicNotFound />;
    return (
        <SurfaceAssistScope surface={surface}>
            <Box pb="xl">
                <PageLevelFilterRow assist={<SurfaceAssistToolbar surface={surface} />} />

                <PageHeaderComponent
                    icon={Activity}
                    eyebrow="Token Usage"
                    title="Activity"
                    subtitle="Where consumption actually goes, heaviest prompts first — the people, departments, and models behind the token volume and its cost. Open a row to read the conversation."
                />

                <SurfaceActivityTab
                    accent="teal"
                    intro="Open a row to read the full prompt."
                    persistenceKey="spend-activity"
                    columns={TOKEN_USAGE_INTERACTION_COLUMNS}
                    orderBy={HEAVIEST_FIRST}
                    truncationDescriptor="heaviest"
                />
            </Box>
        </SurfaceAssistScope>
    );
}
